<script setup lang="ts">
import { IStoriesTrack, IBannersTrack, ICity } from '@caks/shared/modules/i'
import { useAccountStore } from '@caks/shared/store/store.account'
import { getProfileData } from '@caks/shared/api/user.route'
import { IFoodMenu, IFoodItem, IStory } from '@caks/shared/modules/i'
import { ModalCentered, ModalAside, ModalWithoutClose } from '~/components/modal'
import { IiCart } from '@caks/shared/modules/ii'
import { OrderPickup, OrderNewAddress, OrderPromo } from '~/components/order'
import { Component as ComponentType } from 'vue'
import { ModalElement } from '~/types/components.types'
import { AuthConfirm, AuthPhone } from '~/components/auth'

const route = useRoute()
const router = useRouter()

const store = useAccountStore()

const modal = ref<ModalElement>()
const asideModal = ref<ModalElement>()
const cityModal = ref<ModalElement>()
const phone = ref('')

const noPaddingModal = computed(() => {
  const noPaddingContent = ['story', 'new-address']

  //@ts-ignore
  return noPaddingContent.includes(visibleModal.value)
})

type ModalVisible = 'food' | 'order' | 'new-address' | 'promo' | 'auth' | 'city' | 'authConfirm'

const visibleModal = ref<ModalVisible | null>(null)

watch(
  () => store.asideModalActive,
  (newVal) => {
    console.log('newVal', newVal)
    if (newVal) asideModal.value?.show()
  }
)

watch(
  () => store.needChangeLocation,
  () => {
    if (store.needChangeLocation && !store.cart.length) {
      openAddressPicker()
      store.setNeedChangeLocation(false)
    }
  }
)

const closeModal = (mode?: 'default' | 'cart' | 'city') => {
  if (!mode) mode = 'default'

  switch (mode) {
    case 'default':
      modal.value?.hide()
      break
    case 'cart':
      asideModal.value?.hide()
      break
    case 'city':
      cityModal.value?.hide()
      break
    default:
      modal.value?.hide()
      cityModal.value?.hide()
  }

  visibleModal.value = null
}

onMounted(async () => {
  if (route.query.state === 'cart') {
    asideModal.value?.show()
  }
})

const addToCart = () => {
  console.log('addToCart')
  if (store.profile) {
    if (store.cart.length) {
      closeModal()
    } else {
      visibleModal.value = 'order'
    }
  } else {
    visibleModal.value = 'auth'
  }
}

const openPromoModal = () => {
  visibleModal.value = 'promo'
  modal.value?.show()
}

const openAddressPicker = () => {
  visibleModal.value = 'order'
  modal.value?.show()
}

const makeOrder = () => {
  if (store.selectedLocation && store.profile) {
    asideModal.value?.hide()
    router.push('/order')
  } else {
    visibleModal.value = !store.profile ? 'auth' : 'new-address'
    modal.value?.show()
  }
}

type ModalVisibleContent = {
  [K in ModalVisible]: { component: ComponentType; props: any }
}

const modalVisibleContent = computed((): ModalVisibleContent => {
  return {
    food: {
      component: IFoodItem,
      props: {
        desktop: true,
      },
    },
    order: {
      component: OrderPickup,
      props: {
        desktop: true,
      },
    },
    'new-address': {
      component: OrderNewAddress,
      props: {},
    },
    promo: {
      component: OrderPromo,
      props: {},
    },
    city: {
      component: ICity,
      props: {},
    },
    auth: {
      component: AuthPhone,
      props: {
        phone: phone.value,
        onToNextStep: () => {
          store.phone = phone.value.replace(/\D/g, '')
          visibleModal.value = 'authConfirm'
        },
        ['onUpdate:phone']: (value: string) => {
          phone.value = value
        },
      },
    },
    authConfirm: {
      component: AuthConfirm,
      props: {
        onToNextStep: async () => {
          visibleModal.value = 'order'

          const { data: profileData, status } = await getProfileData()
          if (status === 'OK') {
            store.setProfile(profileData)
          }
        },
      },
    },
  }
})

const isMount = ref(false)

onMounted(() => {
  isMount.value = true
})

const onAddAddress = () => {
  visibleModal.value = 'order'
}

const onStoryWatched = (id: number) => {
  if (!store.watchedStoriesIds.includes(id)) {
    store.watchedStoriesIds.push(id)
    localStorage.setItem('wathed-stories', JSON.stringify(store.watchedStoriesIds))
  }
}

watch(
  () => store.selectedLocation,
  () => {
    store.tempCart.forEach((item) => {
      // @ts-ignore
      store.addToCart(item)
    })

    store.tempCart = []
  }
)
</script>

<template>
  <modal-centered @hide="router.push('/')" :no-padding="noPaddingModal" ref="modal">
    <component
      v-if="visibleModal"
      :is="modalVisibleContent[visibleModal].component"
      v-bind="modalVisibleContent[visibleModal].props"
      @change-visible="(value: ModalVisible) => (visibleModal = value)"
      @close="closeModal"
      @add-to-cart="addToCart"
      @add-address="onAddAddress"
      @watch-story="onStoryWatched"
    />
  </modal-centered>
  <modal-aside ref="asideModal" color="#fafafa">
    <ii-cart
      desktop
      @close="closeModal('cart')"
      @make-order="makeOrder"
      @open-promo="openPromoModal"
      @open-address="openAddressPicker"
    />
  </modal-aside>
</template>

<style scoped lang="scss"></style>
